import React, { useLayoutEffect, useState, useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { Color } from "../utils/variables";

export const SmothLineChart = ({
  data,
  id = "smoothLineChartDiv",
  size = 400,
  color = "green",
  sameDate = false,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 500);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    if (data.length === 0) return;
    // Disable animations to prevent potential issues
    am4core.options.autoDispose = true;
    am4core.useTheme(am4themes_animated);

    // Create chart instance
    let chart = am4core.create(id, am4charts.XYChart);

    // Add data
    chart.data = data;

    // Set default font for the entire chart
    chart.fontFamily = "Oxygen-Regular";

    // Hide amCharts logo/watermark
    chart?.logo && (chart.logo.disabled = true);
    // Add padding to chart
    chart.paddingRight = 10;
    chart.paddingTop = 20;
    chart.paddingLeft = 10;

    // Create category axis for hours
    let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = sameDate ? "hour" : "date";
    categoryAxis.title.text = "";
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.labels.template.fill = am4core.color(Color(color, 7));
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.labels.template.fontFamily = "Oxygen-Regular";
    categoryAxis.title.fill = am4core.color(Color(color, 7));
    categoryAxis.title.fontSize = 12;
    categoryAxis.title.fontFamily = "Oxygen-Regular";

    // Create value axis
    let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = "";
    valueAxis.min = 0;
    valueAxis.max = Math.max(...data.map((item) => item.value)) * 1.1; // Add 10% to max value
    valueAxis.renderer.grid.template.strokeDasharray = "2,2";
    valueAxis.renderer.grid.template.strokeOpacity = 0.5;
    valueAxis.renderer.grid.template.stroke = am4core.color(Color(color, 7));
    valueAxis.renderer.labels.template.fill = am4core.color(Color(color, 7));
    valueAxis.renderer.labels.template.fontSize = 12;
    valueAxis.renderer.labels.template.fontFamily = "Oxygen-Regular";
    valueAxis.renderer.opposite = false;
    valueAxis.renderer.labels.template.dx = -10;
    valueAxis.renderer.labels.template.dy = -10;
    valueAxis.renderer.inside = true;
    valueAxis.renderer.maxLabelPosition = 0.98;
    valueAxis.title.fill = am4core.color(Color(color, 7));
    valueAxis.title.fontSize = 12;
    valueAxis.title.fontFamily = "Oxygen-Regular";

    // Create series
    let series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.categoryX = sameDate ? "hour" : "date";
    series.dataFields.valueY = "value";
    series.strokeWidth = 2;
    series.stroke = am4core.color(Color(color, 7));
    series.tensionX = 0.8;

    // Add bullets
    let bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.fill = am4core.color(Color(color, 7));
    bullet.circle.strokeWidth = 0;
    bullet.circle.radius = 3;
    bullet.circle.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    // Add tooltip
    bullet.tooltipText = "{valueY}";
    let tooltip = bullet.createChild(am4core.Tooltip);
    tooltip.background.fill = am4core.color("#000000");
    tooltip.background.strokeWidth = 0;
    tooltip.background.cornerRadius = 4;
    tooltip.label.fill = am4core.color("#FFFFFF");
    tooltip.label.fontSize = 14;
    tooltip.label.fontFamily = "Oxygen-Regular";
    tooltip.label.padding(8, 8, 8, 8);
    tooltip.disabled = data.length <= 15;

    // Add hover state for bullets
    let bulletHoverState = bullet.circle.states.create("hover");
    bulletHoverState.properties.scale = 1.5;
    bulletHoverState.properties.fill = am4core.color(Color(color, 7));
    bulletHoverState.properties.strokeWidth = 2;
    bulletHoverState.properties.stroke = am4core.color(Color(color, 7));

    // Add value labels next to bullets
    let label = bullet.createChild(am4core.Label);
    label.text = "{valueY}";
    label.fontSize = 12;
    label.fontFamily = "Oxygen-Regular";
    label.fontWeight = "400";
    label.fill = am4core.color(Color(color, 7));
    label.horizontalCenter = "left";
    label.verticalCenter = "middle";
    label.dx = 13;
    label.dy = -3;
    label.disabled = isMobile || data.length > 15;

    // Create states for the entire bullet
    let bulletState = bullet.states.create("hover");
    bulletState.properties.scale = 1.5;

    // Update label visibility when isMobile changes
    const updateLabelVisibility = () => {
      label.disabled = isMobile || data.length > 15;
    };
    updateLabelVisibility();

    // For series tooltip (in case it is triggered)
    series.tooltipText = "{valueY}";
    series.tooltip.disabled = data.length <= 15;

    // Cleanup function
    return () => {
      if (chart) {
        chart.dispose();
      }
    };
  }, [data, isMobile]); // Added isMobile to dependency array

  return <div id={id} style={{ width: "100%", height: `${size}px` }} />;
};

export default SmothLineChart;
